import axios from 'axios';
import { toast } from 'sonner';

// Token storage helpers — used as a fallback when cookies are blocked
// (third-party cookie restrictions in incognito mode + cross-domain deploys
// like cut2pro.n-gal.com calling an emergentagent.com backend).
const ACCESS_KEY = 'c2p_access_token';
const REFRESH_KEY = 'c2p_refresh_token';
export function setAuthTokens(at, rt) {
  try {
    if (at) localStorage.setItem(ACCESS_KEY, at);
    if (rt) localStorage.setItem(REFRESH_KEY, rt);
  } catch (e) { console.debug('[caught]', e); }
}
export function clearAuthTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch (e) { console.debug('[caught]', e); }
}
function getAccessToken() {
  try { return localStorage.getItem(ACCESS_KEY) || ''; } catch { return ''; }
}
function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY) || ''; } catch { return ''; }
}

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Phase 4 — auto-inject X-Factory-Id header on every request.
// CRITICAL: the active factory selection MUST be scoped to the logged-in user.
// If we used a global `active_factory_id` key, then User-A's selection would
// leak to User-B when they share a device, or differ silently across devices
// (causing the "same login, no data on one device" bug).
//
// We use a user-scoped key: `c2p_factory_<user_id>`. The current user_id is
// stored as `c2p_active_uid` (set/cleared by AuthContext on login/logout).
api.interceptors.request.use((config) => {
  try {
    const uid = localStorage.getItem('c2p_active_uid');
    if (uid) {
      const fid = localStorage.getItem(`c2p_factory_${uid}`);
      if (fid) config.headers['X-Factory-Id'] = fid;
    }
    // Authorization Bearer fallback for cross-domain deploys where third-party
    // cookies get blocked (incognito mode on Edge/Chrome/Safari). Backend
    // accepts both cookie and Bearer auth.
    const at = getAccessToken();
    if (at && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${at}`;
    }
  } catch (e) { console.debug('[caught]', e); }
  return config;
});

// ── 402 handler: SUBSCRIPTION_EXPIRED / TENANT_SUSPENDED ────────────────────
// One-shot guard prevents redirect loops if the blocked page itself fires a
// request that also 402s.
const BLOCKED_CODES = new Set(['SUBSCRIPTION_EXPIRED', 'TENANT_SUSPENDED']);
const BLOCKED_EXEMPT = new Set(['/subscription-blocked', '/login', '/signup', '/billing']);
let blocked402Shown = false;

function handle402(code, detail, subStatus) {
  if (blocked402Shown) return;
  const path = window.location.pathname;
  if (BLOCKED_EXEMPT.has(path)) return;
  blocked402Shown = true;
  try {
    sessionStorage.setItem('c2p_subscription_blocked', JSON.stringify({ code, detail, sub_status: subStatus || '' }));
  } catch (e) { console.debug('[caught]', e); }
  window.location.replace('/subscription-blocked');
}

// ── 402 handler: PLAN_UPGRADE_REQUIRED / MAX_USERS_REACHED ──────────────────
// These do NOT redirect — they fire a CustomEvent so the global UpgradeModal
// can intercept and display the right message without losing the user's context.
const UPGRADE_CODES = new Set(['PLAN_UPGRADE_REQUIRED', 'MAX_USERS_REACHED']);

// Once per page, notify the user about a terminal auth failure so the UI is
// never silently empty (fixes "UI loads but shows no data" on one device).
let sessionErrorShown = false;
function notifySessionError() {
  if (sessionErrorShown) return;
  // Don't nag users on public auth pages.
  try {
    const path = window.location.pathname;
    if (path === '/login' || path === '/signup' || path === '/subscription-blocked') return;
  } catch (e) { console.debug('[caught]', e); }
  sessionErrorShown = true;
  // Tokens are dead — clear them so we don't keep retrying with a stale Bearer.
  clearAuthTokens();
  try {
    toast.error('Your session has expired on this device. Redirecting to sign-in…', { duration: 3500 });
  } catch (e) { console.debug('[caught]', e); }
  // Small delay so the toast is visible
  setTimeout(() => {
    try {
      const path = window.location.pathname;
      // Don't redirect away from public/auth pages.
      if (path === '/login' || path === '/signup' || path === '/subscription-blocked') return;
      // Stash the current path so users return to where they were after re-login
      sessionStorage.setItem('postLoginRedirect', path + window.location.search);
      window.location.replace('/login');
    } catch (e) { console.debug('[caught]', e); }
  }, 1200);
}

// Harvest tokens from auth responses so the Bearer fallback always has fresh
// values. Triggers on /auth/phone-login, /auth/login, /auth/set-pin,
// /auth/register, /auth/refresh, /auth/google-session.
api.interceptors.response.use(
  res => {
    try {
      const url = res.config?.url || '';
      const data = res.data || {};
      if (url.includes('/auth/') && (data.access_token || data.refresh_token)) {
        setAuthTokens(data.access_token, data.refresh_token);
      }
      if (url.includes('/auth/logout')) {
        clearAuthTokens();
      }
    } catch (e) { console.debug('[caught]', e); }
    return res;
  },
  async (error) => {
    const status = error.response?.status;
    const respCode = error.response?.data?.code;
    // 402 → subscription expired or tenant suspended → redirect to blocked page
    if (status === 402 && BLOCKED_CODES.has(respCode)) {
      handle402(respCode, error.response?.data?.detail || '', error.response?.data?.status || '');
      return Promise.reject(error);
    }
    // 402 → plan upgrade required or user limit hit → show UpgradeModal in-place
    if (status === 402 && UPGRADE_CODES.has(respCode)) {
      try {
        const payload = error.response?.data || {};
        window.dispatchEvent(new CustomEvent('c2p:upgrade-required', { detail: payload }));
      } catch (e) { console.debug('[caught]', e); }
      return Promise.reject(error);
    }
    // 401 → one-shot refresh attempt
    if (status === 401 && !error.config._retry) {
      error.config._retry = true;
      try {
        // Send the refresh token in the body too — cookies may be blocked in
        // incognito on a cross-domain deploy.
        const refreshBody = { refresh_token: getRefreshToken() || '' };
        const r = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
          refreshBody,
          { withCredentials: true }
        );
        if (r?.data?.access_token) setAuthTokens(r.data.access_token, null);
        return api(error.config);
      } catch {
        notifySessionError();
        return Promise.reject(error);
      }
    }
    // 401 after a retry means refresh token is dead too — surface it loudly
    if (status === 401) notifySessionError();
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  // PLAN_UPGRADE_REQUIRED — structured object from plan-gated routes
  if (detail?.code === 'PLAN_UPGRADE_REQUIRED') {
    return detail?.detail
      || 'This feature is not available on your current plan. Go to Billing → Plans to upgrade.';
  }
  // MAX_USERS_REACHED — structured object from POST /users
  if (detail?.code === 'MAX_USERS_REACHED') {
    return detail?.detail || `User limit reached (${detail?.max_users ?? '?'} allowed on your plan). Upgrade to add more.`;
  }
  // iter153 — Lot-qty validator returns
  //   { error: "lot_qty_validation_failed",
  //     message: "...",
  //     violations: [{ kind, color, size, message, ... }, ...] }
  // Surface the first 3 violation messages so the operator sees what to fix.
  if (detail?.error === 'lot_qty_validation_failed' && Array.isArray(detail.violations)) {
    const lines = detail.violations.slice(0, 3).map(v => v.message);
    const more = detail.violations.length > 3 ? ` (+${detail.violations.length - 3} more)` : '';
    return `${detail.message}\n${lines.join('\n')}${more}`;
  }
  // iter155 — Duplicate lot detected (HTTP 409). Tells the operator the
  // ghost lot's number so they can navigate to it.
  if (detail?.error === 'duplicate_lot') {
    return detail.message || 'Duplicate lot detected.';
  }
  if (detail?.msg) return detail.msg;
  if (detail?.message) return detail.message;
  return String(detail);
}

/** Safe parallel fetch — individual 403/failures don't crash the whole page.
 *  401 errors are intentionally treated specially by the interceptor above
 *  (refresh + redirect). 403 "no permission" errors surface a single toast so
 *  managers know which module they can't access instead of seeing blank data. */
let perm403Shown = new Set();
export async function safeAll(...promises) {
  const results = await Promise.allSettled(promises);
  return results.map(r => {
    if (r.status === 'fulfilled') return r.value;
    const status = r.reason?.response?.status;
    const detail = r.reason?.response?.data?.detail;
    if (status === 403 && typeof detail === 'string' && !perm403Shown.has(detail)) {
      perm403Shown.add(detail);
      try { toast.warning(`Permission: ${detail}`, { duration: 4500 }); } catch (e) { console.debug('[caught]', e); }
    }
    return { data: Array.isArray(r.reason?.response?.data) ? [] : (r.reason?.response?.data || null) };
  });
}

/** Download a file with authentication (cookies). Honors the server's
 *  `Content-Disposition` filename when present so backend-controlled
 *  document names (e.g. `Challan_LOT-00001_Bhavna_NBPS07.pdf`) flow through. */
export async function downloadFile(url, filename) {
  try {
    const r = await api.get(url, { responseType: 'blob' });
    // Prefer the server-provided filename
    let serverName = '';
    const cd = r.headers?.['content-disposition'] || r.headers?.['Content-Disposition'];
    if (cd) {
      // Match RFC 5987 (filename*=) first, then plain filename=
      let m = /filename\*=(?:UTF-8''|)([^;]+)/i.exec(cd);
      if (!m) m = /filename="?([^";]+)"?/i.exec(cd);
      if (m) {
        try { serverName = decodeURIComponent(m[1].trim().replace(/^"|"$/g, '')); }
        catch { serverName = m[1].trim().replace(/^"|"$/g, ''); }
      }
    }
    const blob = new Blob([r.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = serverName || filename || 'download';
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error('Download failed:', err);
    throw err;
  }
}

export default api;
